"use client";

import { useMetaPlatform } from "./provider";

export function useListDefaults(
  platform: "META" | "TIKTOK" | "GOOGLE" | "SNAPCHAT" = "META",
) {
  const { trpc, orgSlug } = useMetaPlatform();
  const query = trpc.organization.listCreativeTestingDefaults.useQuery(
    { slug: orgSlug },
    { enabled: true },
  );

  const filtered = query.data?.filter(
    (d: { platform: string }) => d.platform === platform,
  );

  return { ...query, data: filtered };
}

export function useGetDefaultById(defaultId?: string) {
  const { trpc, orgSlug } = useMetaPlatform();
  return trpc.organization.getCreativeTestingDefaultById.useQuery(
    { id: defaultId!, slug: orgSlug },
    { enabled: !!defaultId },
  );
}

export function useCreateDefault() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.createCreativeTestingDefault.useMutation();
}

export function useCreateDefaultFromMetaTarget() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.createCreativeTestingDefaultFromMetaTarget.useMutation();
}

/**
 * TikTok parallel of `useCreateDefaultFromMetaTarget`. Creates a new
 * TikTok defaults record pre-filled from an existing TikTok campaign
 * (and optionally a specific ad group). The result carries origin
 * markers (`tiktokSource*`, `tiktok*Frozen`) so new-batch can auto-enter
 * reuse mode the next time the user loads this default.
 *
 * Hosted on the Meta SDK module because both providers share the same
 * `trpc` client — the TikTok provider doesn't have its own defaults
 * mutations module yet.
 */
export function useCreateDefaultFromTikTokTarget() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.createCreativeTestingDefaultFromTikTokTarget.useMutation();
}

/**
 * Google parallel of `useCreateDefaultFromMetaTarget`. Creates a new Google
 * defaults record pre-filled from an existing Google campaign (and optionally
 * a specific ad group). The result carries origin markers (`googleSource*`,
 * `google*Frozen`) so new-batch can auto-enter reuse mode the next time the
 * user loads this default.
 *
 * Hosted on the Meta SDK module because every provider shares the same `trpc`
 * client — same arrangement as `useCreateDefaultFromTikTokTarget`.
 */
export function useCreateDefaultFromGoogleTarget() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.createCreativeTestingDefaultFromGoogleTarget.useMutation();
}

export function useUpdateDefault() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.updateCreativeTestingDefaultById.useMutation();
}

export function useDeleteDefault() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.deleteCreativeTestingDefault.useMutation();
}

export function useSetActiveDefault() {
  const { trpc } = useMetaPlatform();
  return trpc.organization.setActiveCreativeTestingDefault.useMutation();
}
